interface NamedDesign {
  key: string;
  name: string;
  configName?: string;
  pluginId?: string;
  createdAt?: string;
}

export interface DesignDisambiguator {
  shortId?: string;
  configName?: string;
  /** "Oct 3", or "Oct 3 · 16:01" when another same-name design dates from that day. */
  date?: string;
}

const normalizeName = (name: string) => name.trim().toLowerCase();

const parseDate = (value?: string) => {
  const date = value ? new Date(value) : undefined;
  return date && !Number.isNaN(date.getTime()) ? date : undefined;
};

/**
 * Plugin id minus the account suffix the deploy appends
 * (`checkout-r9re-3d1c1d` → `checkout-r9re`).
 */
export function designShortId(pluginId?: string): string | undefined {
  if (!pluginId) return undefined;
  return pluginId.replace(/^(.+)-[0-9a-f]{6}$/, '$1');
}

/**
 * What tells apart designs that share a name, keyed by design key; designs
 * with a unique name get no entry. Configs of one plugin share its id, so the
 * config name is kept only when another same-name design comes from that plugin.
 */
export function buildDesignDisambiguators(designs: NamedDesign[], locale: string): Map<string, DesignDisambiguator> {
  const groups = new Map<string, NamedDesign[]>();
  designs.forEach((d) => {
    const key = normalizeName(d.name);
    groups.set(key, [...(groups.get(key) || []), d]);
  });

  const dayOf = (date: Date) => date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  const yearDayOf = (date: Date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

  const result = new Map<string, DesignDisambiguator>();
  groups.forEach((group) => {
    if (group.length < 2) return;
    const days = group.map((d) => parseDate(d.createdAt)).map((date) => (date ? yearDayOf(date) : undefined));
    group.forEach((d, i) => {
      const date = parseDate(d.createdAt);
      const sharesDay = !!days[i] && days.filter((day) => day === days[i]).length > 1;
      result.set(d.key, {
        shortId: designShortId(d.pluginId),
        configName: group.some((o) => o !== d && o.pluginId === d.pluginId) ? d.configName : undefined,
        date: date && (sharesDay
          ? `${dayOf(date)} · ${date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false })}`
          : dayOf(date)),
      });
    });
  });
  return result;
}

export const formatDesignDisambiguator = (d: DesignDisambiguator) =>
  [d.shortId, d.configName, d.date].filter(Boolean).join(' · ');
